export type View = "list" | "agenda" | "monthly" | "pinboard";
export type WeekStart = "sun" | "mon";

export type WidgetConfig = {
  apiBaseUrl: string;
  eventDetailBaseUrl: string;
  tag: string[];
  startsAfter?: string;
  startsBefore?: string;
  pageSize: number;
  roundness: string;
  modalRoundness: string;
  showImage: boolean;
  showDetails: boolean;
  showSport: boolean;
  showRecurrence: boolean;
  showDescription: boolean;
  showLocation: boolean;
  showAppLinks: boolean;
  view: View;
  showToolbar: boolean;
  showSearch: boolean;
  showViewSwitcher: boolean;
  showDateNav: boolean;
  showSubtitle: boolean;
  headerTitle: string;
  fullCalendarUrl?: string;
  monthlyWeekStart: WeekStart;
  pinboardColumns: number;
  themeToolbarBg?: string;
  themeLinkColor?: string;
  themeAccentColor?: string;
  themeFontDisplay?: string;
  themeFontBody?: string;
};

export const DEFAULT_PAGE_SIZE = 10;
export const MAX_PAGE_SIZE = 100;

const VALID_VIEWS: readonly View[] = ["list", "agenda", "monthly", "pinboard"];
const DEFAULT_HEADER_TITLE = "Upcoming Events";

const COLOR_PATTERN =
  /^(#[0-9a-fA-F]{3,8}|rgba?\([\d.,%\s/]+\)|hsla?\([\d.,%\s/]+\)|[a-zA-Z]{1,32})$/;

const FONT_STACK_PATTERN = /^[a-zA-Z0-9_\-\s,'"]{1,200}$/;

function parseCsvList(rawValue?: string): string[] {
  if (!rawValue) {
    return [];
  }

  return rawValue
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function parsePageSize(rawValue?: string): number {
  const parsed = Number.parseInt(rawValue ?? "", 10);
  if (Number.isNaN(parsed)) {
    return DEFAULT_PAGE_SIZE;
  }

  return Math.min(Math.max(parsed, 1), MAX_PAGE_SIZE);
}

function getApiBaseUrl(rawValue?: string): string {
  if (!rawValue) {
    return window.location.origin;
  }

  return rawValue.replace(/\/$/, "");
}

function getEventDetailBaseUrl(
  rawValue: string | undefined,
  apiBaseUrl: string,
): string {
  if (!rawValue) {
    return apiBaseUrl;
  }

  return rawValue.replace(/\/$/, "");
}

function parseRoundness(rawValue?: string, fallback = "12px"): string {
  if (!rawValue) {
    return fallback;
  }

  const trimmed = rawValue.trim();

  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    return `${trimmed}px`;
  }

  if (/^\d+(\.\d+)?(px|rem|em|%)$/.test(trimmed)) {
    return trimmed;
  }

  return fallback;
}

function parseBooleanFlag(
  rawValue: string | undefined,
  fallback = true,
): boolean {
  if (rawValue == null) {
    return fallback;
  }

  const normalized = rawValue.trim().toLowerCase();
  if (normalized === "") {
    return true;
  }

  if (["0", "false", "no", "off"].includes(normalized)) {
    return false;
  }

  if (["1", "true", "yes", "on"].includes(normalized)) {
    return true;
  }

  return fallback;
}

function parseView(rawValue?: string): View {
  if (!rawValue) return "list";
  const normalized = rawValue.trim().toLowerCase();
  return (VALID_VIEWS as readonly string[]).includes(normalized)
    ? (normalized as View)
    : "list";
}

function parseHeaderTitle(rawValue: string | undefined): string {
  if (rawValue == null) return DEFAULT_HEADER_TITLE;
  return rawValue;
}

function parseWeekStart(rawValue?: string): WeekStart {
  if (!rawValue) return "sun";
  const normalized = rawValue.trim().toLowerCase();
  return normalized === "mon" ? "mon" : "sun";
}

function parsePinboardColumns(rawValue?: string): number {
  const parsed = Number.parseInt(rawValue ?? "", 10);
  if (Number.isNaN(parsed)) return 4;
  return Math.min(Math.max(parsed, 1), 6);
}

function parseColor(rawValue: string | undefined): string | undefined {
  if (!rawValue) return undefined;
  const trimmed = rawValue.trim();
  if (!trimmed) return undefined;
  return COLOR_PATTERN.test(trimmed) ? trimmed : undefined;
}

function parseFontStack(rawValue: string | undefined): string | undefined {
  if (!rawValue) return undefined;
  const trimmed = rawValue.trim();
  if (!trimmed) return undefined;
  return FONT_STACK_PATTERN.test(trimmed) ? trimmed : undefined;
}

function parseHttpUrl(rawValue: string | undefined): string | undefined {
  if (!rawValue) return undefined;
  const trimmed = rawValue.trim();
  if (!trimmed) return undefined;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return undefined;
    }
    return parsed.toString();
  } catch {
    return undefined;
  }
}

export function readConfig(node: HTMLElement): WidgetConfig {
  const apiBaseUrl = getApiBaseUrl(node.dataset.gitgApiBaseUrl);
  return {
    apiBaseUrl,
    eventDetailBaseUrl: getEventDetailBaseUrl(
      node.dataset.gitgEventDetailBaseUrl,
      apiBaseUrl,
    ),
    tag: parseCsvList(node.dataset.gitgTag ?? node.dataset.gitgSport),
    startsAfter: node.dataset.gitgStartsAfter,
    startsBefore: node.dataset.gitgStartsBefore,
    pageSize: parsePageSize(node.dataset.gitgPageSize),
    roundness: parseRoundness(node.dataset.gitgRoundness),
    modalRoundness: parseRoundness(node.dataset.gitgModalRoundness, "12px"),
    showImage: parseBooleanFlag(node.dataset.gitgShowImage),
    showDetails: parseBooleanFlag(node.dataset.gitgShowDetails),
    showSport: parseBooleanFlag(node.dataset.gitgShowSport),
    showRecurrence: parseBooleanFlag(node.dataset.gitgShowRecurrence),
    showDescription: parseBooleanFlag(node.dataset.gitgShowDescription),
    showLocation: parseBooleanFlag(node.dataset.gitgShowLocation),
    showAppLinks: parseBooleanFlag(node.dataset.gitgShowAppLinks),
    view: parseView(node.dataset.gitgView),
    showToolbar: parseBooleanFlag(node.dataset.gitgShowToolbar, false),
    showSearch: parseBooleanFlag(node.dataset.gitgShowSearch),
    showViewSwitcher: parseBooleanFlag(node.dataset.gitgShowViewSwitcher),
    showDateNav: parseBooleanFlag(node.dataset.gitgShowDateNav),
    showSubtitle: parseBooleanFlag(node.dataset.gitgShowSubtitle),
    headerTitle: parseHeaderTitle(node.dataset.gitgHeaderTitle),
    fullCalendarUrl: parseHttpUrl(node.dataset.gitgFullCalendarUrl),
    monthlyWeekStart: parseWeekStart(node.dataset.gitgMonthlyWeekStart),
    pinboardColumns: parsePinboardColumns(node.dataset.gitgPinboardColumns),
    themeToolbarBg: parseColor(node.dataset.gitgThemeToolbarBg),
    themeLinkColor: parseColor(node.dataset.gitgThemeLinkColor),
    themeAccentColor: parseColor(node.dataset.gitgThemeAccentColor),
    themeFontDisplay: parseFontStack(node.dataset.gitgThemeFontDisplay),
    themeFontBody: parseFontStack(node.dataset.gitgThemeFontBody),
  };
}
